import type { Grid } from './path';
import { isWalkable } from './path';
import { BUILDING_DEFS } from '../content/buildings';
import { PRODUCER_IDS } from '../content/production';
import { UNIT_DEFS, unitDef } from '../content/units';
import type { GameState, UnitId } from './types';

/*
 * פקודות.
 *
 * כל פקודה ניתנת לסריאליזציה מלאה: מספרים בלבד, בלי פונקציות, בלי float ובלי
 * הפניות לאובייקטים. זו הדרישה שתאפשר בהמשך לשלוח אותן ברשת ולשמור אותן כ-replay,
 * ולכן היא נשמרת מהיום הראשון גם כשאין רשת.
 *
 * לכל פקודה יש `tick` (הטיק שבו היא מתבצעת) ו-`seq` לכל שחקן. יחד עם מספר השחקן
 * הם נותנים סדר מלא בתוך טיק, שאינו תלוי בסדר ההגעה.
 */

interface CommandBase {
    readonly tick: number;
    readonly player: number;
    readonly seq: number;
    readonly unitIds: readonly UnitId[];
}

export interface MoveCommand extends CommandBase {
    readonly kind: 'move';
    readonly tileX: number;
    readonly tileY: number;
}

export interface AttackCommand extends CommandBase {
    readonly kind: 'attack';
    readonly targetId: UnitId;
}

export interface StopCommand extends CommandBase {
    readonly kind: 'stop';
}

/**
 * תקוף-נוע: מתקדם ליעד ומחסל כל מה שבדרך.
 * זו הפעולה שבאמת משתמשים בה בקרב — יותר מכל בורר עמדות.
 */
export interface AttackMoveCommand extends CommandBase {
    readonly kind: 'attackMove';
    readonly tileX: number;
    readonly tileY: number;
}

/** הזמנת פריט לתור. `unitIds` ריק — זו פקודה של השחקן, לא של יחידות. */
export interface QueueCommand extends CommandBase {
    readonly kind: 'queue';
    readonly itemKind: number;
    readonly itemType: number;
}

export interface CancelCommand extends CommandBase {
    readonly kind: 'cancel';
    /**
     * איזה תור לבטל. **אינדקס ולא סוג פריט**: מרגע שיש תור לכל מבנה ייצור,
     * "בטל יחידה" אינו מזהה תור אחד.
     */
    readonly queueIndex: number;
}

/** הצבת מבנה שהושלם. */
export interface PlaceCommand extends CommandBase {
    readonly kind: 'place';
    readonly tileX: number;
    readonly tileY: number;
}

/** קביעת נקודת התכנסות למבנה ייצור. `unitIds` ריק — זו פקודה של השחקן. */
export interface SetRallyCommand extends CommandBase {
    readonly kind: 'setRally';
    readonly buildingId: number;
    readonly tileX: number;
    readonly tileY: number;
}

export interface SellCommand extends CommandBase {
    readonly kind: 'sell';
    readonly buildingId: number;
}

/** מתג: מסמן מבנה לתיקון או מבטל את הסימון. */
export interface RepairCommand extends CommandBase {
    readonly kind: 'repair';
    readonly buildingId: number;
}

export type Command =
    | MoveCommand
    | AttackCommand
    | AttackMoveCommand
    | StopCommand
    | QueueCommand
    | CancelCommand
    | PlaceCommand
    | SetRallyCommand
    | SellCommand
    | RepairCommand;

/*
 * Omit רגיל על יוניה מאחד את כל החלופות לאובייקט אחד ומאבד את השדות הייחודיים
 * (`tileX` נעלם). הצורה המתפלגת שומרת על כל חלופה בנפרד.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** פקודה כפי שהממשק מנסח אותה — בלי הטיק, השחקן והמונה, שנקבעים במקור הפקודות. */
export type CommandDraft = DistributiveOmit<Command, 'tick' | 'player' | 'seq'>;

/** סדר מלא ויציב: טיק, ואז שחקן, ואז מונה הפקודות של אותו שחקן. */
export function compareCommands(a: Command, b: Command): number {
    if (a.tick !== b.tick) return a.tick - b.tick;
    if (a.player !== b.player) return a.player - b.player;
    return a.seq - b.seq;
}

/**
 * נקראת גם מ-`step` וגם מה-UI, כדי ששניהם לא יוכלו לחלוק על מה מותר.
 * ממשק שמראה פקודה חוקית שהסימולציה דוחה הוא באג שקשה מאוד לאתר.
 */
/** האם הצמד סוג-תור/סוג-פריט מצביע על משהו שקיים בקטלוג. */
function isKnownItem(itemKind: number, itemType: number): boolean {
    if (!Number.isInteger(itemType) || itemType < 0) return false;
    if (itemKind === 0) return itemType < BUILDING_DEFS.length;
    if (itemKind === 1) return itemType < UNIT_DEFS.length;
    return false;
}

export function isLegal(state: GameState, grid: Grid, command: Command): boolean {
    if (command.player < 0 || command.player >= state.players.length) return false;

    /*
     * פקודות כלכלה אינן פקודות של יחידות: אין להן `unitIds`, ובדיקת הבעלות
     * שלהן היא על השחקן עצמו. לכן הן נבדקות לפני בדיקת היחידות.
     */
    switch (command.kind) {
        /*
         * **`itemKind`/`itemType` נבדקים כאן ולא רק בממשק.** ‏`step` קורא
         * ל-`buildingDef`/`unitDef`, ושניהם **זורקים** על אינדקס לא מוכר — כלומר
         * פקודה פגומה אחת מפילה את הסימולציה בתוך rAF, בלי מסך שגיאה. היום זה לא
         * נגיש מהממשק, אבל `isLegal` מתועדת כשער היחיד, והשער הבא שייכנס דרכה
         * הוא מקור פקודות מהרשת שאסור לתת בו אמון.
         */
        case 'queue':
            return isKnownItem(command.itemKind, command.itemType);
        case 'cancel':
            return (
                Number.isInteger(command.queueIndex) &&
                command.queueIndex >= 0 &&
                command.queueIndex < PRODUCER_IDS.length
            );
        case 'place':
            return isWalkable(grid, command.tileX, command.tileY);
        case 'setRally': {
            // רק על מבנה שלך, ורק לאריח שאפשר להגיע אליו.
            const source = state.buildings.find((b) => b.id === command.buildingId);
            if (!source || source.owner !== command.player || source.hp <= 0) return false;
            return isWalkable(grid, command.tileX, command.tileY);
        }
        case 'repair': {
            // אותו שער כמו מכירה: רק מבנה שלך, וחי.
            const building = state.buildings.find((b) => b.id === command.buildingId);
            return building !== undefined && building.owner === command.player && building.hp > 0;
        }
        case 'sell': {
            const building = state.buildings.find((b) => b.id === command.buildingId);
            return building !== undefined && building.owner === command.player && building.hp > 0;
        }
        default:
            break;
    }

    if (command.unitIds.length === 0) return false;

    /*
     * אפשר לפקד רק על יחידות **שלך** — אבל מזהה שכבר לא קיים אינו פוסל.
     *
     * קודם נדרש שכל מזהה יימצא, ולכן יחידה אחת שנפלה בקרב ביטלה בשקט את הפקודה
     * של כל הכוח: השחקן בחר שמונה, שתיים מתו, הוא הקיש על יעד — והשש הנותרות לא
     * זזו. עם השהיית הקלט של שני טיקים זה קרה גם לבחירה שהייתה תקפה בדיוק ברגע
     * ההקשה. **תכונת האבטחה נשמרת במלואה:** יחידה קיימת שאינה שלך עדיין פוסלת,
     * ופקודה שלא נשאר בה אף מזהה חי נדחית.
     */
    let owned = 0;
    for (const id of command.unitIds) {
        const unit = state.units.find((u) => u.id === id);
        if (unit === undefined) continue;
        if (unit.owner !== command.player) return false;
        owned++;
    }
    if (owned === 0) return false;

    switch (command.kind) {
        case 'move':
        case 'attackMove': {
            /*
             * **גבולות המפה קודם.** ‏`isWalkable` מחזיר `false` גם על אריח מחוץ
             * למפה, וחריג האוויר בלע את שניהם: יעד עם `tileX = -2` היה נגלש
             * לסוף השורה הקודמת, ו-`tileY` מעבר לגובה שלח את המטוס אל מחוץ
             * לגבולות — שם אין לו שום קלאמפ בתנועה.
             */
            if (
                command.tileX < 0 ||
                command.tileY < 0 ||
                command.tileX >= grid.w ||
                command.tileY >= grid.h
            ) {
                return false;
            }
            if (isWalkable(grid, command.tileX, command.tileY)) return true;
            /*
             * **יעד מעל מים או סלע חוקי אם יש בכוח מטוס.** יחידות הקרקע שבבחירה
             * פשוט לא ימצאו מסלול ויעצרו נקי — ההתנהגות שכבר מתועדת ב-`resolvePaths`.
             * בלי זה פקודה לכוח מעורב הייתה נדחית כולה, ומטוס לא היה יכול לחצות
             * אגם — כלומר היתרון היחיד שלו היה מבוטל בשער החוקיות.
             */
            return command.unitIds.some((id) => {
                const unit = state.units.find((u) => u.id === id);
                return unit !== undefined && unitDef(unit.type).layer === 'air';
            });
        }
        case 'attack': {
            // מטרה חייבת להתקיים ולהיות של מישהו אחר. ירי עצמי אינו פקודה חוקית.
            const target = state.units.find((u) => u.id === command.targetId);
            return target !== undefined && target.owner !== command.player;
        }
        case 'stop':
            return true;
        default:
            return false;
    }
}
